import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  Add,
  InfoOutlined,
  EventNote,
  Schedule,
  Security,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

import useCallVisitForm from "./ManageCallVisitModal/useCallVisitForm";
import {
  DAYS,
  SectionHeader,
  SectionPaper,
} from "./ManageCallVisitModal/shared";
import CustomInput from "../../../../../FormFields/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import AssignEmployee from "../../../../../../screens/ClientsScreen/AssignEmployee";
import FundingModal from "../../../../Funding/FundingModal";
import CarePlanPickerDialog from "../../../../CarePlan/Dialog";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import TimeSlots from "../../../../../../screens/VisitPlanScreen/VisitPlanModal/EditSchedule/TimeSlots";
import DaySelector from "../../../../../../screens/VisitPlanScreen/VisitPlanModal/EditSchedule/DaySelector";
import { CustomDateTime, CustomTextArea } from "../../../../../CustomForm";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";

const SAFETY_ITEMS = [
  { label: "Allow Photos", name: "allow_photos", desc: "Enable image evidence" },
  { label: "Share with Family", name: "share_with_family", desc: "Update family portal" },
  { label: "Mandatory Notes", name: "mandatory_notes", desc: "Required visit notes" },
];

const ManageCallVisitModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const form = useCallVisitForm(onClose, () => setActiveTab(1));

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleFinish = () => {
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          maxHeight: "92vh",
          height: "92vh",
          overflow: "hidden",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventNote sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1A1A2E" fontSize="1.1rem">
              Create Call of Visit
            </Typography>
            <Typography variant="caption" color="#6B7280" fontSize="0.75rem">
              Add visit details & assign carers
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#9CA3AF",
            "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ opacity: 0.4, flexShrink: 0 }} />

      <Box
        sx={{
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 52,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              py: 1.2,
              minHeight: 52,
              color: "#64748B",
              "&.Mui-selected": { color: "#2469F0" },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              bgcolor: "#2469F0",
            },
          }}
        >
          <Tab
            icon={<EventNote sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Create Visit"
          />
          <Tab
            icon={<PeopleIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Assign Employee"
            disabled={!form.createdVisitId}
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          {form.isLoadingCarePlans ? (
            <DialogContent
              dividers
              sx={{
                py: 6,
                display: "flex",
                justifyContent: "center",
                bgcolor: "#FAFBFC",
                flex: 1,
                minHeight: 0,
              }}
            >
              <CircularProgress />
            </DialogContent>
          ) : (
            <Box
              component="form"
              onSubmit={form.handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <DialogContent
                dividers
                sx={{
                  background: "#FAFBFC",
                  p: 2.5,
                  overflow: "auto",
                  flex: 1,
                  minHeight: 0,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#F1F5F9",
                    borderRadius: 10,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#CBD5E1",
                    borderRadius: 10,
                    "&:hover": { bgcolor: "#94A3B8" },
                  },
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ flex: "1 1 48%", minWidth: "320px" }}>
                    <SectionPaper>
                      <SectionHeader
                        icon={EventNote}
                        iconColor={theme.palette.primary.main}
                        bgColor="#F8FAFC"
                        title="Visit Details"
                        chip={
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              bgcolor: "#D1FAE5",
                              color: "#039855",
                            }}
                          />
                        }
                      />
                      <Box sx={{ p: 2.5 }}>
                        <Stack spacing={2}>
                          <CustomInput
                            label="Visit Name *"
                            name="visit_name"
                            value={form.formData.visit_name || ""}
                            onChange={form.handleChange}
                            required
                            fullWidth
                          />

                          <Box>
                            <CustomSelect
                              options={form.carePlanOptions}
                              label="Care Plan *"
                              name="care_plan"
                              value={form.formData.care_plan}
                              onChange={form.handleChange}
                              required
                              fullWidth
                              placeholder="Select Care Plan"
                            />
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                              onClick={() => form.setShowCarePlanDialog(true)}
                              sx={{
                                mt: 0.5,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                color: theme.palette.primary.main,
                              }}
                            >
                              Browse Care Plan
                            </Button>
                          </Box>

                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Box sx={{ flex: 2 }}>
                              <CustomSelect
                                options={form.paymentServiceOptions}
                                label="Service *"
                                name="payment_service"
                                value={form.formData.payment_service}
                                onChange={form.handleChange}
                                required
                                fullWidth
                                placeholder="Select Funding Profile"
                              />
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                                onClick={() => form.setShowFundingModal(true)}
                                sx={{
                                  mt: 0.5,
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  color: theme.palette.primary.main,
                                }}
                              >
                                Add Funding Profile
                              </Button>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <CustomInput
                                label="Carers *"
                                name="carers_required"
                                type="number"
                                value={form.formData.carers_required || 1}
                                onChange={form.handleChange}
                              />
                            </Box>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="#475467"
                              sx={{ mb: 0.5, display: "block" }}
                            >
                              CHECKLISTS
                            </Typography>
                            <MultiSelect
                              options={form.checkListOptions}
                              value={form.selectedCheckList}
                              onChange={form.setSelectedCheckList}
                              placeholder="Select checklists..."
                            />
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="#475467"
                              sx={{ mb: 0.5, display: "block" }}
                            >
                              PERSONALIZED CHECKS
                            </Typography>
                            <Stack spacing={0.5}>
                              {form.formData.personalized_checks?.map((input, index) => (
                                <Stack
                                  key={input.id}
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <Box flexGrow={1}>
                                    <CustomInput
                                      placeholder="New check..."
                                      value={input.check_name}
                                      onChange={(e) =>
                                        form.handlePersonalizedCheck(index, e)
                                      }
                                      size="small"
                                    />
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => form.handleRemoveInput(input.id)}
                                    sx={{ color: "#EF4444", p: 0.5 }}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ))}
                            </Stack>
                            <Button
                              size="small"
                              startIcon={<Add sx={{ fontSize: 16 }} />}
                              onClick={form.handleAddInput}
                              sx={{
                                mt: 0.5,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                color: theme.palette.primary.main,
                              }}
                            >
                              Add Check
                            </Button>
                          </Box>

                          <CustomTextArea
                            label="Type Instructions Here"
                            name="support_instructions"
                            value={form.formData.support_instructions || ""}
                            onChange={form.handleChange}
                            rows={3}
                          />

                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              p: 1.5,
                              bgcolor: "#F8FAFC",
                              borderRadius: "6px",
                              border: "1px dashed #D0D5DD",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <CustomDateTime
                                name="start_time"
                                label="Start Date *"
                                onChange={form.handleChange}
                                value={form.formData.start_time || ""}
                                required
                                format="YYYY-MM-DD HH:mm"
                              />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CustomCheckbox
                                label="Indefinite"
                                name="continue_indefinitely"
                                checked={form.formData.continue_indefinitely || false}
                                onClick={(c) =>
                                  form.handleCheckBox("continue_indefinitely", c)
                                }
                              />
                            </Box>
                          </Box>

                          {!form.isContinueIndifinite && (
                            <CustomDateTime
                              name="end_time"
                              label="End Date"
                              onChange={form.handleChange}
                              value={form.formData.end_time || ""}
                              format="YYYY-MM-DD HH:mm"
                            />
                          )}
                        </Stack>
                      </Box>
                    </SectionPaper>
                  </Box>

                  <Box sx={{ flex: "1 1 48%", minWidth: "320px" }}>
                    <Stack spacing={2}>
                      <SectionPaper>
                        <SectionHeader
                          icon={Schedule}
                          iconColor="#F59E0B"
                          bgColor="#F8FAFC"
                          title="Scheduling"
                          chip={
                            <Chip
                              label={`${form.selectedDays.length} days`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.6rem",
                                fontWeight: 600,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                              }}
                            />
                          }
                        />
                        <Box sx={{ p: 2.5 }}>
                          <Stack spacing={2}>
                            <CustomSelect
                              label="Frequency"
                              name="repeat_every"
                              options={form.visitTypeOptions}
                              value={form.formData.repeat_every}
                              onChange={form.handleVisitTypeChange}
                              fullWidth
                              placeholder="Select Frequency"
                            />

                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: "#F8FAFC",
                                borderRadius: "6px",
                                border: "1px solid #E2E8F0",
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color="#475467"
                                sx={{
                                  mb: 1,
                                  display: "block",
                                  fontSize: "0.65rem",
                                  letterSpacing: "0.3px",
                                  textTransform: "uppercase",
                                }}
                              >
                                SELECT DAYS
                              </Typography>

                              <DaySelector
                                daysOfWeek={DAYS}
                                selectedDays={form.selectedDays}
                                weeklyAssignment={[]}
                                onDaySelect={form.handleDaySelection}
                                disabled={true}
                              />

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 1,
                                  mb: 1.5,
                                }}
                              >
                                <CustomCheckbox
                                  label="Custom times per day"
                                  checked={form.isCustomTime}
                                  onClick={() =>
                                    form.setIsCustomTime(!form.isCustomTime)
                                  }
                                />
                                <Tooltip title="Set unique times for each day">
                                  <InfoOutlined sx={{ fontSize: 14, color: "#94A3B8" }} />
                                </Tooltip>
                              </Box>

                              {form.isCustomTime && form.selectedDays.length > 0 && (
                                <TimeSlots
                                  selectedDays={form.selectedDays}
                                  customTimeData={form.customTimeData}
                                  onTimeChange={form.handleCustomTimeChange}
                                  onAddSlot={form.handleAddSlot}
                                  onRemoveSlot={form.handleRemoveSlot}
                                  weeklyAssignment={[]}
                                />
                              )}

                              <Box
                                sx={{
                                  mt: 1.5,
                                  pt: 1.5,
                                  borderTop: "1px solid #E2E8F0",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="#475467"
                                >
                                  Total Weekly Hours
                                </Typography>
                                <Chip
                                  label={form.totalWeeklyHours}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: alpha("#EF4444", 0.08),
                                    color: "#EF4444",
                                  }}
                                />
                              </Box>
                            </Box>

                            {!form.isCustomTime && (
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <CustomTimePicker
                                  label="Global From"
                                  name="time_required_from"
                                  value={form.formData.time_required_from || ""}
                                  onChange={form.handleChange}
                                  size="small"
                                />
                                <CustomTimePicker
                                  label="Global To"
                                  name="time_required_to"
                                  value={form.formData.time_required_to || ""}
                                  onChange={form.handleChange}
                                  size="small"
                                />
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <CustomCheckbox
                                label="Time Critical?"
                                name="time_critical"
                                checked={form.formData.time_critical || false}
                                onClick={(c) =>
                                  form.handleCheckBox("time_critical", c)
                                }
                              />
                              {!form.isTimeCritical && (
                                <CustomInput
                                  label="Tol. (min)"
                                  name="time_tolerance"
                                  type="number"
                                  value={form.formData.time_tolerance || 60}
                                  onChange={form.handleChange}
                                  size="small"
                                />
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      </SectionPaper>

                      <SectionPaper>
                        <SectionHeader
                          icon={Security}
                          iconColor="#2196F3"
                          bgColor="#F0F7FF"
                          title="Safety & Compliance"
                          subtitle="Manage security protocols"
                        />
                        <Box sx={{ p: 2.5 }}>
                          <Stack spacing={1.5}>
                            {SAFETY_ITEMS.map((item) => (
                              <Box
                                key={item.name}
                                sx={{
                                  p: 1.5,
                                  borderRadius: "6px",
                                  border: "1px solid #E2E8F0",
                                  bgcolor: form.formData[item.name]
                                    ? alpha(theme.palette.primary.main, 0.02)
                                    : "#FAFBFC",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    borderColor: form.formData[item.name]
                                      ? theme.palette.primary.main
                                      : "#94A3B8",
                                  },
                                }}
                              >
                                <CustomCheckbox
                                  label={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color="#1A1A2E"
                                      >
                                        {item.label}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="#6B7280"
                                        sx={{ fontSize: "0.65rem" }}
                                      >
                                        {item.desc}
                                      </Typography>
                                    </Box>
                                  }
                                  name={item.name}
                                  checked={form.formData[item.name] || false}
                                  onClick={(c) =>
                                    form.handleCheckBox(item.name, c)
                                  }
                                />
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </SectionPaper>
                    </Stack>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  bgcolor: "#FFFFFF",
                  borderTop: "1px solid #E2E8F0",
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={form.isSubmitting}
                  startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "6px",
                    borderColor: "#D0D5DD",
                    color: "#344054",
                    px: 3,
                    py: 0.75,
                    "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={form.isSubmitting}
                  startIcon={
                    form.isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "6px",
                    bgcolor: "#2469F0",
                    px: 3,
                    py: 0.75,
                    "&:hover": {
                      bgcolor: "#1A5BD4",
                      boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
                    },
                  }}
                >
                  {form.isSubmitting ? "Creating..." : "Create & Continue"}
                </Button>
              </DialogActions>
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && form.createdVisitId && (
        <>
          <DialogContent
            dividers
            sx={{
              background: "#FAFBFC",
              p: 2.5,
              overflow: "auto",
              flex: 1,
              minHeight: 0,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": {
                bgcolor: "#F1F5F9",
                borderRadius: 10,
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#CBD5E1",
                borderRadius: 10,
                "&:hover": { bgcolor: "#94A3B8" },
              },
            }}
          >
            <AssignEmployee
              visitId={form.createdVisitId}
              clientId={form.clientID}
            />
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#FFFFFF",
              borderTop: "1px solid #E2E8F0",
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={handleFinish}
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
                bgcolor: "#2469F0",
                px: 3,
                py: 0.75,
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
                },
              }}
            >
              Done
            </Button>
          </DialogActions>
        </>
      )}

      <FundingModal
        show={form.showFundingModal}
        onHide={() => form.setShowFundingModal(false)}
        clientID={form.clientID}
      />
      <CarePlanPickerDialog
        open={form.showCarePlanDialog}
        onClose={() => form.setShowCarePlanDialog(false)}
        clientID={form.clientID}
        onSelect={form.handleCarePlanSelect}
      />
    </Dialog>
  );
};

export default ManageCallVisitModal;